import {Button, Grid, Switch, TextField, Theme, Typography} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/DeleteOutlined';
import {makeStyles} from '@material-ui/styles';
import React, {useEffect, useState} from 'react';
import {LoadingOverlay} from '../../../components/LoadingOverlay';
import {OPERATOR} from '../../../graphql/common.datatypes';
import {ResourceType} from '../../../graphql/resources.datatypes';
import anyLoading from '../../../utils/anyLoading';
import {getHandleFieldChange} from '../../../utils/formHelpers';
import getResources from '../../Resources/getResources.api';
import {formatGraphQlDate} from '../../../utils/formatters';

const useStyles = makeStyles((theme: Theme) => ({
  root: {},
  content: {
    padding: theme.spacing(0, 3),
    flexGrow: 1
  },
  contentSection: {
    padding: theme.spacing(2, 0)
  },
  contentSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    cursor: 'pointer'
  },
  contentSectionContent: {},
  formGroup: {
    padding: theme.spacing(2, 0)
  },
  fieldGroup: {
    display: 'flex',
    alignItems: 'center'
  },
  field: {
    marginTop: 0,
    marginBottom: 0
  },
  flexGrow: {
    flexGrow: 1
  },
  addButton: {
    marginLeft: theme.spacing(1)
  },
  tags: {
    marginTop: theme.spacing(1)
  },
  radioGroup: {},
  buttonIcon: {
    marginRight: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(3),
    '& > * + *': {
      marginTop: theme.spacing(2)
    }
  }
}));

export interface FilterValues {
  name: string,
  tour_resource_id: number,
  hotel_resource_id: number,
  start_date?: Date | string,
  end_date?: Date | string,
  booking_form_url: string,
  default_authorize_members_true: boolean,
  default_authorize_members_false: boolean,
  validated_true: boolean,
  validated_false: boolean,
  disabled_true: boolean,
  disabled_false: boolean,
  locked_true: boolean,
  locked_false: boolean,
  // hasPrimaryLeader: boolean,
  // hasLeader: boolean,
}

export const GroupSearchFilter = (props: { applyFilters: (filters: FilterValues) => void, filters: FilterValues, initialFilters: FilterValues }) => {
  const {filters, initialFilters} = props;
  const classes = useStyles();
  const [formState, setFormState] = useState({...filters});

  const toursData = getResources({AND: [{column: 'resource_type', operator: OPERATOR.EQ, value: ResourceType.TOUR},]});
  const hotelData = getResources({AND: [{column: 'resource_type', operator: OPERATOR.EQ, value: ResourceType.HOTEL},]});
  const loadingData = anyLoading(toursData, hotelData);

  useEffect(() => {
    setFormState(props.filters)
  }, [props.filters]);

  const handleClear = () => {
    props.applyFilters(initialFilters);
  };

  const handleFieldChange = getHandleFieldChange(setFormState);

  const applyFilters = () => {
    props.applyFilters(formState);
  };

  return (
    <React.Fragment>
      <LoadingOverlay loading={loadingData.loading}>
        <div className={classes.content}>
          <div className={classes.contentSection}>

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Name"
                  name="name"
                  onChange={handleFieldChange}
                  value={formState.name}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Tour"
                  name="tour_resource_id"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={formState.tour_resource_id}
                  variant="outlined"
                  InputLabelProps={{shrink: true}}
                >
                  <option value={-1}>Any Tour</option>
                  {toursData.data && toursData.data.allResources && toursData.data.allResources.map((tour) => (
                    <option key={tour.id} value={tour.id}>
                      {tour.internal_name}
                    </option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Hotel"
                  name="hotel_resource_id"
                  onChange={handleFieldChange}
                  select
                  // eslint-disable-next-line react/jsx-sort-props
                  SelectProps={{native: true}}
                  value={formState.hotel_resource_id}
                  variant="outlined"
                  InputLabelProps={{shrink: true}}
                >
                  <option value={-1}>Any Hotel</option>
                  {hotelData.data && hotelData.data.allResources && hotelData.data.allResources.map((hotel) => (
                    <option key={hotel.id} value={hotel.id}>
                      {hotel.internal_name}
                    </option>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Typography component="p" gutterBottom variant="overline">
                  Date Range
                </Typography>
                <Grid container spacing={3}>
                  <Grid item md={6}>
                    <TextField
                        className={classes.field}
                        defaultValue={formState.start_date ? formatGraphQlDate(formState.start_date) : ''}
                        fullWidth
                        label="Start date"
                        name="start_date"
                        onChange={handleFieldChange}
                        type="date"
                        InputLabelProps={{shrink: true}}
                        variant="outlined"
                    />
                  </Grid>
                  <Grid item md={6}>
                    <TextField
                        className={classes.field}
                        defaultValue={formState.end_date ? formatGraphQlDate(formState.end_date) : ''}
                        fullWidth
                        label="End date"
                        name="end_date"
                        onChange={handleFieldChange}
                        type="date"
                        InputLabelProps={{shrink: true}}
                        variant="outlined"
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid component="label" container alignItems="center" spacing={1}>
                  <Grid item xs={6}><Typography variant="h5">Default Authorize Members?</Typography></Grid>
                  <Grid item><Typography variant="h5">Yes</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.default_authorize_members_true}
                      color="secondary"
                      edge="start"
                      name="default_authorize_members_true"
                      onChange={handleFieldChange}
                      value={formState.default_authorize_members_true}
                    />
                  </Grid>
                  <Grid item><Typography variant="h5">No</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.default_authorize_members_false}
                      color="secondary"
                      edge="start"
                      name="default_authorize_members_false"
                      onChange={handleFieldChange}
                      value={formState.default_authorize_members_false}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid component="label" container alignItems="center" spacing={1}>
                  <Grid item xs={4}><Typography variant="h5">Validated?</Typography></Grid>
                  <Grid item><Typography variant="h5">Yes</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.validated_true}
                      color="secondary"
                      edge="start"
                      name="validated_true"
                      onChange={handleFieldChange}
                      value={formState.validated_true}
                    />
                  </Grid>
                  <Grid item><Typography variant="h5">No</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.validated_false}
                      color="secondary"
                      edge="start"
                      name="validated_false"
                      onChange={handleFieldChange}
                      value={formState.validated_false}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid component="label" container alignItems="center" spacing={1}>
                  <Grid item xs={4}><Typography variant="h5">Disabled?</Typography></Grid>
                  <Grid item><Typography variant="h5">Yes</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.disabled_true}
                      color="secondary"
                      edge="start"
                      name="disabled_true"
                      onChange={handleFieldChange}
                      value={formState.disabled_true}
                    />
                  </Grid>
                  <Grid item><Typography variant="h5">No</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.disabled_false}
                      color="secondary"
                      edge="start"
                      name="disabled_false"
                      onChange={handleFieldChange}
                      value={formState.disabled_false}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Grid component="label" container alignItems="center" spacing={1}>
                  <Grid item xs={4}><Typography variant="h5">Locked?</Typography></Grid>
                  <Grid item><Typography variant="h5">Yes</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.locked_true}
                      color="secondary"
                      edge="start"
                      name="locked_true"
                      onChange={handleFieldChange}
                      value={formState.locked_true}
                    />
                  </Grid>
                  <Grid item><Typography variant="h5">No</Typography></Grid>
                  <Grid item>
                    <Switch
                      checked={formState.locked_false}
                      color="secondary"
                      edge="start"
                      name="locked_false"
                      onChange={handleFieldChange}
                      value={formState.locked_false}
                    />
                  </Grid>
                </Grid>
              </Grid>

            </Grid>

          </div>
        </div>
        <div className={classes.actions}>
          <Button id="clear" fullWidth onClick={handleClear} variant="contained">
            <DeleteIcon className={classes.buttonIcon}/>
            Clear
          </Button>
          <Button id="submit" color="primary" fullWidth variant="contained" onClick={applyFilters}>
            Apply filters
          </Button>
        </div>
      </LoadingOverlay>
    </React.Fragment>
  );
};

export default GroupSearchFilter;